import {
  AxisConfig,
  getAnnotationCenter,
} from 'features/kestrelLite/utils/annotations'
import { Eye, EyeOff, Focus } from 'lucide-react'
import React, { FC, useCallback, useMemo } from 'react'
import { Vector3 } from 'three'
import { KVAnnotation, KVInspectionArea } from 'types'

import {
  AnnotationListHeader,
  AnnotationListItem,
  AnnotationListPanel,
  AnnotationListTitle,
  AnnotationName,
  AnnotationScrollContainer,
  FlyToButton,
  SmallCheckbox,
  ToggleLabel,
  ToggleRow,
  ToggleSection,
  UnitToggleButton,
  UnitToggleGroup,
  VisibilityToggle,
} from './annotationListStyles'

export type AreaUnit = 'm' | 'ft'

interface AnnotationListProps {
  annotations: KVAnnotation[]
  inspectionAreas: KVInspectionArea[]
  axisConfig: AxisConfig
  onFlyTo: (target: Vector3) => void
  showAnnotations: boolean
  onToggleVisibility: () => void
  showInspectionAreas: boolean
  onToggleInspectionAreas: () => void
  showLabels: boolean
  onToggleLabels: (show: boolean) => void
  showArea: boolean
  onToggleArea: (show: boolean) => void
  areaUnit: AreaUnit
  onAreaUnitChange: (unit: AreaUnit) => void
}

export const AnnotationList: FC<AnnotationListProps> = ({
  annotations,
  inspectionAreas,
  axisConfig,
  onFlyTo,
  showAnnotations,
  onToggleVisibility,
  showInspectionAreas,
  onToggleInspectionAreas,
  showLabels,
  onToggleLabels,
  showArea,
  onToggleArea,
  areaUnit,
  onAreaUnitChange,
}) => {
  const sortedAnnotations = useMemo(() => {
    return [...annotations]
      .filter((a) => a.geometry)
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
  }, [annotations])

  const visibleInspectionAreas = useMemo(() => {
    return inspectionAreas.filter(
      (ia) => ia.vertices && ia.vertices.length >= 3
    )
  }, [inspectionAreas])

  const handleFlyTo = useCallback(
    (annotation: KVAnnotation) => {
      const center = getAnnotationCenter(annotation, axisConfig)
      if (center) {
        onFlyTo(center)
      }
    },
    [axisConfig, onFlyTo]
  )

  // Show panel if there are either annotations or inspection areas
  if (sortedAnnotations.length === 0 && visibleInspectionAreas.length === 0)
    return null

  return (
    <AnnotationListPanel>
      {sortedAnnotations.length > 0 && (
        <AnnotationListHeader>
          <AnnotationListTitle>
            Annotations ({sortedAnnotations.length})
          </AnnotationListTitle>
          <VisibilityToggle
            $visible={showAnnotations}
            onClick={onToggleVisibility}
            data-tooltip={
              showAnnotations ? 'Hide annotations' : 'Show annotations'
            }>
            {showAnnotations ? <Eye size={18} /> : <EyeOff size={18} />}
          </VisibilityToggle>
        </AnnotationListHeader>
      )}

      <ToggleSection>
        {visibleInspectionAreas.length > 0 && (
          <ToggleRow>
            <ToggleLabel>
              Inspection Areas ({visibleInspectionAreas.length})
            </ToggleLabel>
            <SmallCheckbox
              type='checkbox'
              checked={showInspectionAreas}
              onChange={onToggleInspectionAreas}
            />
          </ToggleRow>
        )}
        {sortedAnnotations.length > 0 && (
          <>
            <ToggleRow>
              <ToggleLabel>Show Labels</ToggleLabel>
              <SmallCheckbox
                type='checkbox'
                checked={showLabels}
                onChange={(e) => onToggleLabels(e.target.checked)}
                disabled={!showAnnotations && !showInspectionAreas}
              />
            </ToggleRow>
            <ToggleRow>
              <ToggleLabel>Show Area Labels</ToggleLabel>
              <SmallCheckbox
                type='checkbox'
                checked={showArea}
                onChange={(e) => onToggleArea(e.target.checked)}
                disabled={!showAnnotations || !showLabels}
              />
            </ToggleRow>
            <ToggleRow>
              <ToggleLabel>Area Unit</ToggleLabel>
              <UnitToggleGroup>
                <UnitToggleButton
                  $active={areaUnit === 'm'}
                  onClick={() => onAreaUnitChange('m')}>
                  m²
                </UnitToggleButton>
                <UnitToggleButton
                  $active={areaUnit === 'ft'}
                  onClick={() => onAreaUnitChange('ft')}>
                  ft²
                </UnitToggleButton>
              </UnitToggleGroup>
            </ToggleRow>
          </>
        )}
      </ToggleSection>

      {sortedAnnotations.length > 0 && (
        <AnnotationScrollContainer>
          {sortedAnnotations.map((annotation, index) => {
            const name = annotation.label || annotation.type || annotation.id
            return (
              <AnnotationListItem key={`${annotation.id}-${index}`}>
                <AnnotationName>{name}</AnnotationName>
                <FlyToButton
                  onClick={() => handleFlyTo(annotation)}
                  data-tooltip={`Fly to ${name}`}>
                  <Focus size={14} />
                </FlyToButton>
              </AnnotationListItem>
            )
          })}
        </AnnotationScrollContainer>
      )}
    </AnnotationListPanel>
  )
}
